import { projectApi } from '@/api/project';
import { buildDraftPreviewHtml } from '@/lib/domUtils';
import type { WebpageDraft, WebpageDraftListItem, WebpageDraftStatus } from '@/types/project';

export const TECHNICAL_GENERATION_SYNC_CHANGE_NOTE = 'Synced current draft before AI generation.';

export const isTechnicalGenerationSyncChangeNote = (value?: string | null) =>
  /^synced current draft before ai generation\.?$/i.test(String(value || '').trim());

export const isDraftVersionConflictError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : String((error as { message?: string } | null | undefined)?.message || error || '');

  return (
    /draft version conflict/i.test(message) ||
    message.includes('鑽夌鐗堟湰鍐茬獊') ||
    message.includes('璇峰埛鏂版渶鏂拌崏绋垮啀淇敼') ||
    message.includes('最新草稿') ||
    message.includes('草稿版本冲突')
  );
};

export interface PendingDraftReviewState {
  currentDraft: WebpageDraft;
  pendingDraft: WebpageDraft;
  beforeHtml: string;
  afterHtml: string;
}

export interface EditableDraftResolution {
  editableDraft: WebpageDraft | null;
  pendingReview: PendingDraftReviewState | null;
  saveBaseVersion: number | null;
}

const STALE_DRAFT_PAGE_SIZE = 100;
const RECENT_DRAFT_STATUS_TTL_MS = 60_000;
const recentDraftStatuses = new Map<string, { status: WebpageDraftStatus; expiresAt: number }>();
const inflightEditableDraftResolutions = new Map<number, Promise<EditableDraftResolution>>();
const waitForDraftWriteRetry = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

const pruneRecentDraftStatuses = () => {
  const now = Date.now();
  recentDraftStatuses.forEach(({ expiresAt }, draftId) => {
    if (expiresAt <= now) {
      recentDraftStatuses.delete(draftId);
    }
  });
};

const getRecentDraftStatus = (
  draftId: number | string | null | undefined,
): WebpageDraftStatus | null => {
  if (draftId == null) return null;

  pruneRecentDraftStatuses();
  return recentDraftStatuses.get(String(draftId))?.status ?? null;
};

export const rememberRecentDraftStatus = (
  draftId: number | string | null | undefined,
  status: WebpageDraftStatus,
) => {
  if (draftId == null) return;

  pruneRecentDraftStatuses();
  recentDraftStatuses.set(String(draftId), {
    status,
    expiresAt: Date.now() + RECENT_DRAFT_STATUS_TTL_MS,
  });
};

const discardPendingDrafts = async (drafts: Array<Pick<WebpageDraftListItem, 'id'>>) => {
  const settled = await Promise.allSettled(
    drafts.map((draft) =>
      projectApi.updateDraftStatus({
        draftId: draft.id,
        draftStatus: 'ABANDONED',
      }),
    ),
  );

  return drafts
    .filter((_, index) => settled[index].status === 'fulfilled')
    .map((draft) => draft.id);
};

export const cleanupSupersededPendingDrafts = async (
  userProjectId: number,
  confirmedVersion: number | null | undefined,
  drafts?: WebpageDraftListItem[],
) => {
  if (confirmedVersion == null) return [];

  const pendingDrafts =
    drafts?.filter((draft) => draft.draftStatus === 'PENDING') ||
    (
      await projectApi.listDrafts({
        userProjectId,
        pageNumber: 1,
        pageSize: STALE_DRAFT_PAGE_SIZE,
        draftStatuses: ['PENDING'],
      })
    ).list ||
    [];

  const stalePendingDrafts = pendingDrafts.filter((draft) => draft.version <= confirmedVersion);
  if (stalePendingDrafts.length === 0) return [];

  return discardPendingDrafts(stalePendingDrafts);
};

export const resolveEditableDraft = async (
  userProjectId: number,
): Promise<EditableDraftResolution> => {
  const existingResolution = inflightEditableDraftResolutions.get(userProjectId);
  if (existingResolution) {
    return existingResolution;
  }

  const resolutionPromise = (async (): Promise<EditableDraftResolution> => {
    const [draftListResponse, latestPendingCandidate, latestConfirmedCandidate] = await Promise.all(
      [
        projectApi.listDrafts({
          userProjectId,
          pageNumber: 1,
          pageSize: 100,
          draftStatuses: ['PENDING', 'CONFIRMED', 'ABANDONED'],
        }),
        projectApi.getLatestDraft(userProjectId, ['PENDING']),
        projectApi.getLatestDraft(userProjectId, ['CONFIRMED']),
      ],
    );

    const latestResolvedDrafts = [latestPendingCandidate, latestConfirmedCandidate]
      .filter((draft): draft is WebpageDraft => Boolean(draft))
      .sort((left, right) => {
        if (right.version !== left.version) {
          return right.version - left.version;
        }

        return new Date(right.createTime).getTime() - new Date(left.createTime).getTime();
      });

    let pendingDraft =
      latestResolvedDrafts.find((draft) => draft.draftStatus === 'PENDING') ?? null;
    let confirmedDraft =
      latestResolvedDrafts.find((draft) => draft.draftStatus === 'CONFIRMED') ?? null;
    let recentlyConfirmedDraft: WebpageDraft | null = null;

    if (confirmedDraft?.version != null) {
      const clearedDraftIds = await cleanupSupersededPendingDrafts(
        userProjectId,
        confirmedDraft.version,
        draftListResponse.list,
      );

      if (pendingDraft && clearedDraftIds.includes(pendingDraft.id)) {
        pendingDraft = null;
      }
    }

    if (pendingDraft) {
      const recentStatus = getRecentDraftStatus(pendingDraft.id);
      if (recentStatus === 'CONFIRMED' && pendingDraft.content) {
        recentlyConfirmedDraft = {
          ...pendingDraft,
          draftStatus: 'CONFIRMED',
        };
        pendingDraft = null;
      } else if (recentStatus && recentStatus !== 'PENDING') {
        pendingDraft = null;
      }
    }

    if (!confirmedDraft && recentlyConfirmedDraft?.content) {
      confirmedDraft = recentlyConfirmedDraft;
    }

    const latestEditableVersion =
      [pendingDraft?.version, confirmedDraft?.version].reduce<number | null>((max, version) => {
        if (typeof version !== 'number') return max;
        return max == null ? version : Math.max(max, version);
      }, null) ?? null;

    if (
      pendingDraft?.content &&
      confirmedDraft?.content &&
      pendingDraft.version > confirmedDraft.version
    ) {
      return {
        editableDraft: confirmedDraft,
        pendingReview: {
          currentDraft: confirmedDraft,
          pendingDraft,
          beforeHtml: buildDraftPreviewHtml(confirmedDraft.content),
          afterHtml: buildDraftPreviewHtml(pendingDraft.content),
        },
        saveBaseVersion: latestEditableVersion ?? pendingDraft.version,
      };
    }

    if (confirmedDraft?.content) {
      return {
        editableDraft: confirmedDraft,
        pendingReview: null,
        saveBaseVersion: latestEditableVersion ?? confirmedDraft.version,
      };
    }

    if (pendingDraft?.content) {
      return {
        editableDraft: pendingDraft,
        pendingReview: null,
        saveBaseVersion: latestEditableVersion ?? pendingDraft.version,
      };
    }

    return {
      editableDraft: null,
      pendingReview: null,
      saveBaseVersion: null,
    };
  })();

  inflightEditableDraftResolutions.set(userProjectId, resolutionPromise);

  try {
    return await resolutionPromise;
  } finally {
    inflightEditableDraftResolutions.delete(userProjectId);
  }
};

export const saveConfirmedDraftSnapshot = async (
  userProjectId: number,
  content: string,
  changeNote: string,
  options?: {
    suppressErrorToast?: boolean;
    retries?: number;
    minBaseVersion?: number | null;
  },
): Promise<WebpageDraft> => {
  const maxRetries = Math.max(1, options?.retries ?? 4);
  let attempt = 0;

  while (attempt < maxRetries) {
    attempt += 1;

    const [resolution, latestDraft] = await Promise.all([
      resolveEditableDraft(userProjectId),
      projectApi.getLatestDraft(userProjectId),
    ]);

    const baseVersionCandidates = [
      options?.minBaseVersion,
      resolution.saveBaseVersion,
      latestDraft?.draftStatus === 'ABANDONED' ? null : latestDraft?.version,
      resolution.editableDraft?.version,
    ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    const baseVersion = baseVersionCandidates.length
      ? Math.max(...baseVersionCandidates)
      : undefined;

    try {
      return await projectApi.saveDraft(
        {
          userProjectId,
          content,
          changeNote,
          draftStatus: 'CONFIRMED',
          ...(baseVersion != null ? { baseVersion } : {}),
        },
        { suppressErrorToast: options?.suppressErrorToast ?? true },
      );
    } catch (error) {
      if (attempt >= maxRetries || !isDraftVersionConflictError(error)) {
        throw error;
      }

      await waitForDraftWriteRetry(120 * attempt);
    }
  }

  throw new Error('Failed to save the latest confirmed draft snapshot.');
};
